import { useEffect, useRef, useCallback } from 'react';
import useStores from 'hooks/use-stores.hooks';
import { useLocalStorageState } from 'ahooks';

const domainToIdleSessionTimeout: Record<string, number> = {
  'visa.com': 1000 * 60 * 60 * 2, // 2 hours
  'env0.com': 1000 * 60 * 60 * 20 // 20 hours, so we can catch issues in this domain in dev/bors/prod in our day to day
};

const idleResetEvents = ['mousedown', 'keydown', 'touchstart'] as const;

export const useIdleSessionLogout = () => {
  const { authStore } = useStores();
  const [authResult] = useLocalStorageState<{ idTokenPayload: { email?: string } }>('authResult');

  const lastActivityTimeRef = useRef<number>(Date.now());
  const timeoutIdRef = useRef<number | null>(null);

  const email = authResult?.idTokenPayload?.email;
  const domain = email ? email.substring(email.indexOf('@') + 1) : undefined;
  const timeout = domain ? domainToIdleSessionTimeout[domain] : undefined;

  const resetTimer = useCallback(() => {
    lastActivityTimeRef.current = Date.now();
  }, []);

  const setupEventListeners = useCallback(() => {
    idleResetEvents.forEach(event => {
      window.addEventListener(event, resetTimer);
    });
  }, [resetTimer]);

  const cleanupEventListeners = useCallback(() => {
    idleResetEvents.forEach(event => {
      window.removeEventListener(event, resetTimer);
    });
  }, [resetTimer]);

  const checkIdleTimeout = useCallback(() => {
    const currentTime = Date.now();
    const idleTime = currentTime - lastActivityTimeRef.current;

    if (timeout && idleTime >= timeout) {
      authStore.logout();
    }
  }, [timeout, authStore]);

  useEffect(() => {
    if (!timeout) return;

    setupEventListeners();
    resetTimer();

    timeoutIdRef.current = window.setInterval(checkIdleTimeout, 30000);

    return () => {
      cleanupEventListeners();
      if (timeoutIdRef.current !== null) {
        clearInterval(timeoutIdRef.current);
        timeoutIdRef.current = null;
      }
    };
  }, [timeout, setupEventListeners, cleanupEventListeners, resetTimer, checkIdleTimeout]);
};
